import { useCallback, useEffect, useState } from 'react'
import { Hash, Menu, Users } from 'lucide-react'
import { hasPermission, Permission, type Message } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { useAppStore } from '@/store/app'
import { usePreferencesStore } from '@/store/preferences'
import { EmptyState, Tooltip } from '@/components/ui'
import { MessageList } from '@/components/chat/MessageList'
import { Composer } from '@/components/chat/Composer'
import { PinnedMessages } from '@/components/chat/PinnedMessages'
import { useGuildPermissions } from '@/hooks/selectors'

/**
 * O estado do campo de escrita mora acima, na tela principal.
 *
 * Motivo: o menu do botão direito sobre uma pessoa também aparece na barra
 * lateral, que é irmã desta tela e não filha. Com o contexto criado aqui, o
 * item "Mencionar" existia na lista da direita e sumia na lista de quem está em
 * chamada, apesar de ser o mesmo menu.
 */
export function ChatView({
  channelId,
  replyTo,
  onClearReply,
  registerHandle,
  onAbrirMenu,
  onAbrirMembros,
}: {
  channelId: string
  replyTo: Message | null
  onClearReply: () => void
  registerHandle: (handle: { focus: () => void; insertText: (text: string) => void }) => void
  /** Abre a gaveta de canais. Só existe em tela estreita. */
  onAbrirMenu?: () => void
  onAbrirMembros?: () => void
}) {
  const channel = useAppStore((s) => s.channels[channelId])
  const showMemberList = usePreferencesStore((s) => s.ui.showMemberList)
  const setUi = usePreferencesStore((s) => s.setUi)
  const minhasPermissoes = useGuildPermissions(channel?.guildId ?? null)

  /** Mensagem a alcançar, escolhida na lista de fixadas. */
  const [alvo, setAlvo] = useState<string | null>(null)

  // Trocar de canal descarta o pedido: a mensagem era de outra conversa.
  useEffect(() => {
    setAlvo(null)
  }, [channelId])

  const limparAlvo = useCallback(() => setAlvo(null), [])

  if (!channel) {
    return (
      <div className="flex flex-1 items-center justify-center bg-surface-base">
        <EmptyState
          icon={<Hash />}
          title="Nenhum canal selecionado"
          description="Escolha um canal na barra lateral para começar a conversar."
        />
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-1 bg-surface-base">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 px-3 shadow-sm sm:px-4">
          {/* Em tela larga as colunas estão à vista e não há o que abrir. */}
          <button
            type="button"
            onClick={onAbrirMenu}
            aria-label="Abrir canais"
            className="-ml-1 flex size-9 shrink-0 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary lg:hidden"
          >
            <Menu className="size-5" />
          </button>

          <Hash className="size-5 shrink-0 text-content-muted" />
          <h1 className="shrink-0 font-semibold text-content-primary">{channel.name}</h1>

          {channel.topic ? (
            <>
              <div className="h-5 w-px shrink-0 bg-line" />
              <p className="min-w-0 truncate text-[13px] text-content-secondary">
                {channel.topic}
              </p>
            </>
          ) : null}

          <div className="ml-auto flex shrink-0 items-center gap-1">
            <PinnedMessages
              channelId={channelId}
              guildId={channel.guildId}
              podeGerenciar={hasPermission(minhasPermissoes, Permission.MANAGE_MESSAGES)}
              onIrParaMensagem={setAlvo}
            />

            {/*
              Dois botões para a mesma ideia, porque o gesto é outro: na tela
              larga a lista fica ou não fica ao lado, e na estreita ela desliza
              por cima e some ao tocar fora.
            */}
            <button
              type="button"
              onClick={onAbrirMembros}
              aria-label="Ver membros"
              className="flex size-8 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary lg:hidden"
            >
              <Users className="size-5" />
            </button>

            <Tooltip content={showMemberList ? 'Ocultar membros' : 'Mostrar membros'}>
              <button
                type="button"
                onClick={() => setUi({ showMemberList: !showMemberList })}
                aria-label="Alternar lista de membros"
                aria-pressed={showMemberList}
                className={cn(
                  'hidden size-8 items-center justify-center rounded-md transition-colors lg:flex',
                  showMemberList
                    ? 'bg-surface-active text-content-primary'
                    : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
                )}
              >
                <Users className="size-5" />
              </button>
            </Tooltip>
          </div>
        </header>

        <MessageList channelId={channelId} alvo={alvo} onAlvoAtendido={limparAlvo} />

        <Composer
          channelId={channelId}
          channelName={channel.name}
          replyTo={replyTo}
          onClearReply={onClearReply}
          registerHandle={registerHandle}
        />
      </div>
    </div>
  )
}
